import React, { useEffect, useState } from 'react';
import LinksButton from 'shared/design-system/components/links-button';
import ProjectData from './_components/project-data';
import { useAppDispatch } from 'core/store/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'antd';
import { getCosts, resetTeamAndCostsData, saveCostsData, saveRevenueProgress, saveTeamData, shiftSchedule } from './_redux/actions';
import { DettagliProgettoDtoStatoEnum, FilterDtoTipologiaEnum } from '@api/client';
import StatusBadge from 'shared/design-system/components/project-status-badge';
import StartRevenuePlanning from './_components/start-revenue-plan';
import RevenuePlan from './_components/revenue-plan';
import useCalculateIndicatori from './_hooks/useCalculateIndicatori';
import { useTranslation } from 'react-i18next';
import { PathEnum } from 'core/router/path.enum';
import useProjectDetails from './_hooks/project-detail/useProjectDetails';
import useTeamManagement from './_hooks/project-detail/useTeamManagement';
import useDateRange from './_hooks/project-detail/useDateRange';
import useProjectActions from './_hooks/project-detail/useProjectActions';
import AppBlocker from './_components/blocker';
import { type unstable_BlockerFunction as BlockerFunction } from 'react-router-dom';
import { trackUserIdGa4 } from 'shared/utils/gtag';
import AuthService from 'core/auth/auth.service';
import { TrackPagesGA4Enum } from 'enums/global.enum';
import { resetBillingPlan } from './_components/billing-plan/_redux/actions';
import PlanByTeamModal from './_components/plan-by-team-modal';
import useGetRoles from './_hooks/project-detail/useRoles';
import { crudGetProject } from 'pages/private/crud/_redux/actions';
import { resetCurrentProject } from 'pages/private/project-list/_redux/actions';
import ModalAction from './_components/modal-action';
import ModalReschedule, { RescheduleData } from './_components/modal-reschedule';
import useGetAllActiveUsers from 'shared/_hooks/useGetAllActiveUsers';

const ProjectDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: any }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const roleList = useGetRoles();
  useGetAllActiveUsers();
  const { projectDetails, setProjectDetails, isPlanning, isWorkInProgress, getProjectDetail } = useProjectDetails();
  const { dateRange, onDateChange, getDefaultDateRange } = useDateRange(projectDetails?.mesiProgetto);
  const { ruoliTeam, handleRoleChange, handleSeniorityChange, handleChangeAmounts, addRole, deleteRole } =
    useTeamManagement(roleList);
  const { indicatori } = useCalculateIndicatori(projectDetails, ruoliTeam);
  const [totalGuValidation, setTotalGuValidation] = useState(true);
  const [totalCostsValidation, setTotalCostsValidation] = useState(true);
  const [draftValidation, setDraftValidation] = useState(false);
  const [costDescriptionValidation, setCostDescriptionValidation] = useState(true);
  const [associatedCostsValidation, setAssociatedCostsValidation] = useState(true);
  const [revenueProgressValidation, setRevenueProgressValidation] = useState(true);
  const [showModalSyncStaff, setShowModalSyncStaff] = useState(false);
  const [showModalReschedule, setShowModalReschedule] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);
  const { createTeam, saveAndConsolidaPlanning, isConfermaEnabled, startPlanningInTable, syncStaffOnIntranet } =
    useProjectActions(
      projectDetails!,
      setProjectDetails,
      id,
      ruoliTeam,
      dateRange,
      handleCloseModal,
      getProjectDetail,
      isPlanning,
      costDescriptionValidation,
      associatedCostsValidation,
      revenueProgressValidation
    );

  useEffect(() => {
    trackUserIdGa4(TrackPagesGA4Enum.PROJECT_DETAIL);
    dispatch(getCosts());

    const handleBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      dispatch(resetBillingPlan());
      dispatch(resetTeamAndCostsData());
      dispatch(saveRevenueProgress(undefined));
    };
  }, []);

  const goToEditProject = () => {
    if (AuthService.isSuperAdmin()) {
      dispatch(crudGetProject(id))
        .unwrap()
        .then((res) => {
          if (res) navigate(`/${PathEnum.PRIVATE}/${PathEnum.CRUD}`);
        });
    } else {
      navigate(`/${PathEnum.PRIVATE}/${PathEnum.CRUD}`);
    }
  };

  let shouldBlock = React.useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      currentLocation.pathname !== nextLocation.pathname &&
      (projectDetails?.staffList?.length ?? 0) > 0 &&
      !draftValidation,
    [draftValidation]
  );

  const canViewRevenuePlan = () => {
    return (projectDetails?.staffList?.length ?? 0) > 0;
  };

  const isProgressTabVisible = () => {
    const projectType = projectDetails?.dettaglioOfferta?.categoria;
    return canViewRevenuePlan() && projectType !== FilterDtoTipologiaEnum.Costo;
  };

  const canReschedule = () => {
    const stato = projectDetails?.dettaglioOfferta?.stato;

    // Stati che NON permettono reschedule (progetti chiusi)
    const closedStates: DettagliProgettoDtoStatoEnum[] = [
      DettagliProgettoDtoStatoEnum.ClosedLost,
      DettagliProgettoDtoStatoEnum.Completed,
      DettagliProgettoDtoStatoEnum.EconomicsClosed,
      DettagliProgettoDtoStatoEnum.ReportingClosed,
    ];

    // Se il progetto è in uno stato "closed", non permettere reschedule
    if (stato && closedStates.includes(stato)) {
      return false;
    }

    const allowedStates: DettagliProgettoDtoStatoEnum[] = [
      DettagliProgettoDtoStatoEnum.WorkInProgress,
      DettagliProgettoDtoStatoEnum.ControlloCdg,
      DettagliProgettoDtoStatoEnum.ControlloCl,
      DettagliProgettoDtoStatoEnum.ClosedWonPlanning,
      DettagliProgettoDtoStatoEnum.Pending,
      DettagliProgettoDtoStatoEnum.Planning,
      DettagliProgettoDtoStatoEnum.IntranetPending,
      DettagliProgettoDtoStatoEnum.PlanningOk,
    ];
    const canRescheduleIfReadOnly = stato && (
      [
        DettagliProgettoDtoStatoEnum.IntranetPending,
        DettagliProgettoDtoStatoEnum.PlanningOk,
      ] as DettagliProgettoDtoStatoEnum[]
    ).includes(stato);

    return stato ? allowedStates.includes(stato) && (!projectDetails?.readOnly || canRescheduleIfReadOnly) : false;
  };

  const handleReschedule = async (data: RescheduleData) => {
    const idProject = projectDetails?.dettaglioOfferta?.id;
    if (!idProject) return;

    const deltaMonths = data.direction === 'backward' ? -data.months : data.months;

    try {
      const result = await dispatch(
        shiftSchedule({
          idProject,
          shiftScheduleInputDto: {
            deltaMonths,
            shiftStaff: data.shiftStaff,
            shiftExternalCost: data.shiftExternalCosts,
            shiftRevenue: data.shiftProgress,
          },
        })
      ).unwrap();

      if (result) {
        // Reset completo dei dati per forzare il refresh delle tabelle Staff e Costi Esterni
        dispatch(saveTeamData(null));
        dispatch(saveCostsData(null));
        dispatch(resetCurrentProject());
        setProjectDetails(null);
        // Chiude il modal solo se la chiamata ha successo
        setShowModalReschedule(false);
      }
    } catch (error) {
      // In caso di errore, il modal rimane aperto e le selezioni dell'utente sono preservate
      console.error('Errore durante lo shift dello schedule:', error);
    }
  };
  return (
    <>
      <AppBlocker shouldBlock={shouldBlock} />
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center col-8">
          <h3>
            ({projectDetails?.dettaglioOfferta?.intranetId}) {projectDetails?.dettaglioOfferta?.name}
          </h3>
          <div className="align-center d-flex">
            <StatusBadge label={true} className="mt-2 ms-3">
              {projectDetails?.dettaglioOfferta?.stato}
            </StatusBadge>
          </div>
        </div>
        {!costDescriptionValidation && (
          <Tooltip
            title={t('project-detail.error-description-other-costs-validation')}
            color="white"
            className="cursor-pointer ms-auto pe-3"
          >
            <i className="fa-solid fa-triangle-exclamation me-1 text-danger" />
          </Tooltip>
        )}

        {projectDetails?.dettaglioOfferta?.stato !== DettagliProgettoDtoStatoEnum.Pending && (
          <div className="d-flex">
            <LinksButton
              className="btn-sm btn-primary btn-teal px-2"
              onClick={goToEditProject}
              isDisabled={!projectDetails?.editDeliveryEnabled && projectDetails?.readOnly}
            >
              <i className="icon-edit" />
            </LinksButton>
            <LinksButton
              className="btn-sm btn-primary btn-teal px-4"
              onClick={() => setShowModalSyncStaff(true)}
              isDisabled={!totalGuValidation || !totalCostsValidation || projectDetails?.readOnly}
            >
              <i className="fas fa-refresh me-2" />
              {t('project-detail.sync-staff-intranet')}
            </LinksButton>
            <LinksButton
              className="btn-sm btn-primary btn-teal px-4"
              onClick={() => setShowModalReschedule(true)}
              isDisabled={!canReschedule()}
            >
              <i className="fas fa-calendar-alt me-2" />
              {t('project-detail.reschedule')}
            </LinksButton>
            <LinksButton
              className="btn-sm btn-primary btn-teal px-4"
              onClick={saveAndConsolidaPlanning}
              isDisabled={!totalGuValidation || !totalCostsValidation || projectDetails?.readOnly}
            >
              <i className="fas fa-check me-2" />
              {isWorkInProgress ? `${t('project-detail.save-planning')}` : `${t('project-detail.send-planning')}`}
            </LinksButton>
          </div>
        )}
      </div>

      <ProjectData projectDetails={projectDetails} />
      {canViewRevenuePlan() ? (
        <RevenuePlan
          projectDetails={projectDetails}
          roleList={roleList}
          idProject={projectDetails?.dettaglioOfferta?.id}
          isWorkInProgress={isWorkInProgress}
          getProjectDetail={getProjectDetail}
          setTotalGuValidation={setTotalGuValidation}
          setTotalCostsValidation={setTotalCostsValidation}
          setDraftValidation={setDraftValidation}
          setCostDescriptionValidation={setCostDescriptionValidation}
          setAssociatedCostsValidation={setAssociatedCostsValidation}
          setRevenueProgressValidation={setRevenueProgressValidation}
        />
      ) : (
        <StartRevenuePlanning
          readOnly={projectDetails?.readOnly}
          projectMonths={projectDetails?.mesiProgetto}
          handleShowModal={handleShowModal}
          startPlanningInTable={startPlanningInTable}
        />
      )}

      <PlanByTeamModal
        showModal={showModal}
        indicatori={indicatori}
        ruoliTeam={ruoliTeam}
        roleList={roleList}
        handleCloseModal={handleCloseModal}
        onDateChange={onDateChange}
        getDefaultDateRange={getDefaultDateRange}
        addRole={addRole}
        handleRoleChange={handleRoleChange}
        handleChangeAmounts={handleChangeAmounts}
        handleSeniorityChange={handleSeniorityChange}
        deleteRole={deleteRole}
        isConfermaEnabled={isConfermaEnabled}
        createTeam={createTeam}
      />
      <ModalAction
        title={t('Sync staff on Intranet')}
        actionLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        action={() => {
          syncStaffOnIntranet();
        }}
        setShow={(value) => setShowModalSyncStaff(value)}
        show={showModalSyncStaff}
      >
        <span className="text-danger">
          <i className="fa-solid fa-triangle-exclamation" /> <b>{t('common.attention')}</b>{' '}
        </span>
        <div className="p-1">
          <p>
            Make sure to save all the changes made by clicking "<b>{t('Save schedule')}</b>" before synchronizing the
            staff on the Intranet.
          </p>
          <p>{t('Are you sure you want to continue?')}</p>
        </div>
      </ModalAction>
      <ModalReschedule
        show={showModalReschedule}
        setShow={setShowModalReschedule}
        onConfirm={handleReschedule}
        projectStatus={projectDetails?.dettaglioOfferta?.stato}
        hasStaff={(projectDetails?.staffList?.length ?? 0) > 0}
        hasExternalCosts={(projectDetails?.otherCosts?.length ?? 0) > 0}
        canShiftProgress={isProgressTabVisible() && (projectDetails?.revenueList?.length ?? 0) > 0}
      />
    </>
  );
};

export default ProjectDetail;
